import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { nanoid } from 'nanoid';
import { preloadVideos } from '@components/AssetPreloader';
import useAsyncEffect from 'use-async-effect';

import { useMytaverse } from '@providers/MytaverseProvider';

import EventsService from '@services/EventsService';
import { IEventParticipant } from '@interfaces/eventParticipant';

import { IAvatarSkin } from '@interfaces/avatarSkin';
import { EventRole, IEvent, IParticipantEvents } from '@interfaces/event';
import {
  IParticipant,
  IParticipantRegion,
  ParticipantState,
} from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { IStreamService } from '@interfaces/streamService';
import ParticipantsService from '@services/ParticipantsService';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { IPointOfInterest } from '@interfaces/pointsOfInterest';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';

import { EventDrawerTabs } from '../../constants';
import {
  useAvatarMessage,
  useEventParticipantData,
  useInitMessage,
  useParticipantsState,
} from './hooks';
import {
  IMytaverseEventContext,
  IWebsocketMessageFlag,
  IUnrealScreenSize,
  SendMutedStateMessageEnum,
  StreamingProviders,
  UpdateParticipantType,
} from './interfaces';
import AnalyticsService from '@services/AnalyticsService';
import { SendToBriefcasePropsTypeFull } from '../../components/DashboardContent/interfaces';
import { useChatState } from '@core/chat';
import PurewebClientOptions from '../../components/DashboardContent/Pureweb/helpers';
import { useInitGameCastStream } from './hooks/streamProviders';
import { usePixelStreamingProvider } from './hooks/pixelStreamingProvider';
import { IFollowerData } from '@interfaces/followers';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { MytaverseLogger } from '@helpers/logger';
import { useCoreweave } from '../../components/DashboardContent/CoreWeave/CoreWeaveProvider';
import { PixelStreaming } from '@tensorworks/libspsfrontend';
import { useGamecast } from '../../components/DashboardContent/GameCast/GameCastProvider';
import { useBackButton } from './hooks/eventListeners';
import { IGamecastVideoStats } from '../../components/DashboardContent/GameCast/interfaces';
import EventFullLoadingPage from '@core/event/pages/EventFullLoadingPage';
import { env } from 'env';

import { AnalyticsType } from '@interfaces/analytics';
import { useEventContext } from '@providers/EventProvider';
import {
  IStartStreamSessionOutput,
  StreamSessionStatus,
} from '@interfaces/gamecast';
import { default as mytaverseEventLogger } from './logger';
import {
  getClientSpecificCanShareScreenOnScreen,
  getClientSpecificCanShareWebCameraOnScreen,
  getClientSpecificCanShowWebCameraOnAvatar,
  getClientSpecificCanSpeak,
  getClientSpecificUserEventPermissions,
  getClientSpecificUserEventRole,
} from '@helpers/clientSpecificUtils';
import { isDesktop } from 'react-device-detect';
import ParticipantScopes from '@constants/scopes';

export const useMytaverseEvent = () =>
  useOutletContext<IMytaverseEventContext>();

export const MytaverseEventProvider = () => {
  const { eventId: currentEventId } = useParams();

  const {
    token,
    idToken,
    userId,
    user,
    userPermissions,
    sendJSONMessageToWebSocket,
    sendMessageToEvent,
    websocketSessionId,
    isLogoutClicked,
    sendMessageToUnreal,
    navigateToEventsPage,
    setUserPermissions,
  } = useMytaverse();

  const [openLeftMenu, setOpenLeftMenu] = useState<boolean>(false);
  const [leftMenuTab, setLeftMenuTab] = useState<EventDrawerTabs>(
    EventDrawerTabs.Locations,
  );
  const [reaction, setReaction] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [openAdminSettingsModal, setOpenAdminSettingsModal] = useState(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [muted, setMuted] = useState(true);
  const [currentEvent, setCurrentEvent] = useState<IEvent>();
  const [eventLoaded, setEventLoaded] = useState<boolean>(false);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [currentRoom, setCurrentRoom] = useState<IRoom | null>(null);
  const [leftMenuScrollHandler, setLeftMenuScrollHandler] = useState<
    ((page: number) => void) | null
  >(null);
  const [developersTerminalMessages, setDevelopersTerminalMessages] = useState<
    string[]
  >([]);
  const [openLeaveRegionDialog, setOpenLeaveRegionDialog] = useState(false);
  const [gameReadyToPlay, setGameReadyToPlay] = useState(true);
  const [openCameraPublisherDialog, setOpenCameraPublisherDialog] =
    useState(false);
  const [ue5WebsocketConnected, setUe5WebsocketConnected] = useState(false);
  const [ue5CoreWeaveDisabled, setUe5CoreWeaveDisabled] = useState(false);
  const [currentRegion, setCurrentRegion] = useState<IParticipantRegion | null>(
    null,
  );
  const [
    currentRoomDolbySpatialAudioScale,
    setCurrentRoomDolbySpatialAudioScale,
  ] = useState<number>(80);
  const [isTeleporting, setIsTeleporting] = useState(false);
  const [poiPreviewSrc, setPoiPreviewSrc] =
    useState<SendToBriefcasePropsTypeFull | null>(null);
  const [initMessageSended, setInitMessageSended] = useState(false);
  const [previousSkin, setPreviousSkin] = useState<IAvatarSkin | null>(null);
  const [currentEventParticipant, setCurrentEventParticipant] =
    useState<IEventParticipant | null>(null);
  const [gameCastStreamRequestSended, setGameCastStreamRequestSended] =
    useState(false);
  const [previousCustomAvatar, setPreviousCustomAvatar] = useState<
    string | null
  >(null);
  const [pointsOfInterest, setPointsOfInterest] = useState<
    IPointOfInterest[] | null
  >(null);
  const [userFiles, setUserFiles] = useState<IPointOfInterest[]>([]);
  const [isGamecastCrash, setIsGamecastCrash] = useState(false);
  const [isGamecastTimeout, setIsGamecastTimeout] = useState(false);
  const [currentParticipant, setCurrentParticipant] = useState<
    IParticipant | undefined
  >();
  const [teleportingToRoom, setTeleportingToRoom] = useState<IRoom | null>(
    null,
  );
  const [teleportEnabled, setTeleportEnabled] = useState(false);
  const [isTeleportingToRoomByUnreal, setIsTeleportingToRoomByUnreal] =
    useState(false);
  const [participants, setParticipants] = useState<IParticipant[]>([]);
  const [loadingNewParticipants, setLoadingNewParticipants] = useState(false);
  const [showAvaturnModal, setShowAvaturnModal] = useState(false);
  const [recreateAvaturnAvatar, setRecreateAvaturnAvatar] = useState(null);
  const [streamService, setStreamService] = useState<IStreamService>();
  const [openTeleportToRoomDialog, setOpenTeleportToRoomDialog] =
    useState(false);
  const [selectedTeleportRoom, setSelectedTeleportRoom] =
    useState<IRoom | null>(null);
  const [dolbyToken, setDolbyToken] = useState<string>('');
  const [streamChatToken, setStreamChatToken] = useState<string>('');
  const [streamChatMemberId, setStreamChatMemberId] = useState<string>('');
  const [enableSoulmachinesAssistant, setEnableSoulmachinesAssistant] =
    useState(false);

  const [skyLinkExperience, setSkyLinkExperience] = useState(false);

  const [showCustomSpotsEditor, setShowCustomSpotsEditor] =
    useState<boolean>(false);
  const [roomSpots, setRoomSpots] = useState<any[]>([]);
  const [selectedCustomizationUeId, setSelectedCustomizationUeId] =
    useState<string | null>(null);
  const [selectedCustomizationTargetSize, setSelectedCustomizationTargetSize] =
    useState<IUnrealScreenSize | null>(null);

  const [showExternalKeyboardOverlay, setShowExternalKeyboardOverlay] =
    React.useState(false);
  const [externalKeyboardConnected, setExternalKeyboardConnected] =
    React.useState(!isDesktop);
  const [movementCollisionIdentified, setMovementCollisionIdentified] =
    React.useState(false);

  const [isUserEnteringEvent, setIsUserEnteringEvent] = useState(false);
  const fallbackScreenshotDownloadUrlRef = useRef(null);
  const [showExitButton, setShowExitButton] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUI, setIsHideWebUI] = useState<IWebsocketMessageFlag>({
    flag: false,
    timestamp: 0,
  });
  const [isHideWebUIExceptAssistant, setIsHideWebUIExceptAssistant] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [isHideAllExceptTopBar, setIsHideAllExceptTopBar] =
    useState<IWebsocketMessageFlag>({
      flag: false,
      timestamp: 0,
    });
  const [isSkyLinkActive, setIsSkyLinkActive] = useState(false);
  const [screenshotDownloadUrl, setScreenshotDownloadUrl] = useState<
    string | null
  >(null);

  const [familiarizeState, setFamiliariseState] = useState(null);

  const {
    REACT_APP_EXTERNAL_EVENT_TAG,
    REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS = 150,
  } = env;

  useEffect(() => {
    if (!REACT_APP_EXTERNAL_EVENT_TAG) {
      return;
    }
    const hasExternalEventTag =
      currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG) === true;

    if (hasExternalEventTag) {
      setIsHideWebUI((prev) => {
        if (prev.flag) {
          return prev;
        }
        return { flag: true, timestamp: Number.MAX_SAFE_INTEGER };
      });
    }
  }, [currentEvent?.eventTags, REACT_APP_EXTERNAL_EVENT_TAG, setIsHideWebUI]);

  useEffect(() => {
    const isA380Event = currentEvent?.eventTags
      ? currentEvent.eventTags.includes('A380')
      : false;
    const params = new URLSearchParams(window.location.search);

    setSkyLinkExperience(
      (params.has('skylink') ? params.get('skylink') === 'true' : false) ||
        isA380Event,
    );
  }, [currentEvent]);

  const [showControls, setShowControls] = useState<boolean>(true);
  const [sharingWindowFullsceen, setSharingWindowFullscreen] = useState(false);
  const [isMinimizedScreenSharing, setIsMinimizedScreenSharing] =
    useState(false);
  const [openDevelopersTerminal, setOpenDevelopersTerminal] = useState(false);
  const [pendingFollowersData, setPendingFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [acceptedFollowersData, setAcceptedFollowersData] = useState<
    IFollowerData[]
  >([]);
  const [preserveAcceptedFollowersId, setPreserveAcceptedFollowersId] =
    useState<string[]>([]);
  const [isParticipantRoomChanged, setIsParticipantRoomChanged] =
    useState(false);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [playVideoUrl, setPlayVideoUrl] = useState<string | null>(null);
  const [coreweavePixelStreaming, setCoreweavePixelStreaming] =
    useState<PixelStreaming | null>(null);
  const gamecastStats = useRef<IGamecastVideoStats | null>(null);
  const [showGamecastStats, setShowGamecastStats] = useState(false);
  const [isUEModalActionInProgress, setIsUEModalActionInProgress] =
    useState(false);

  const ueInitializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const trackEventAnalytics = React.useCallback(
    (type: AnalyticsType, payload: any = {}) => {
      if (websocketSessionId) {
        AnalyticsService.track(type, new Date().getTime(), {
          eventId: currentEventId,
          participantId: userId,
          sessionId: websocketSessionId,
          payload,
        });
      }
    },
    [websocketSessionId],
  );

  const { streamingProvider, setStreamingProvider, pixelStreamingClient } =
    usePixelStreamingProvider({ currentEvent, trackEventAnalytics });
  const gameliftStreamsSessionKeyRef = useRef<string>(nanoid(20));

  const clientOptions = React.useMemo(() => {
    if (
      !streamService ||
      !currentEvent ||
      streamingProvider !== StreamingProviders.Pureweb
    ) {
      return null;
    }

    const purewebClientOptions = new PurewebClientOptions(
      streamService.purewebProjectId,
      streamService.purewebModelId,
    );

    if (currentEvent.region) {
      purewebClientOptions.regionOverride = currentEvent.region;
    }

    return purewebClientOptions;
  }, [currentEvent, streamService, streamingProvider]);

  const { setParticipantState } = useParticipantsState({
    currentParticipantId: userId as string,
    currentEventId,

    currentParticipant,
    setParticipants,

    participants,
    setCurrentParticipant,

    currentRoom,
    setCurrentRoom,

    currentRegion,
    setCurrentRegion,
    setIsParticipantRoomChanged,
    rooms,

    trackEventAnalytics,
  });

  const {
    participantsSound,
    gameSound,
    setGameSoundHandle,
    setParticipantsSoundHandle,
  } = useEventParticipantData({
    currentEvent,
    currentEventParticipant,
  });

  const { launchArgs } = useEventContext();
  const { showNotification, getBadRequestNotification } = useNotification();
  const { setOpen: setOpenChat } = useChatState();
  const { isDebugMode } = useMytaverse();

  const generateNewGameliftStreamsSessionKey = useCallback(() => {
    if (isDebugMode) {
      return '';
    }

    const gameliftStreamsSessionKey = nanoid(20);

    mytaverseEventLogger.log(
      `New gamelift streams session key: ${gameliftStreamsSessionKey}`,
    );
    gameliftStreamsSessionKeyRef.current = gameliftStreamsSessionKey;

    return gameliftStreamsSessionKey;
  }, []);

  const { initMessageHandler } = useInitMessage({
    currentEvent,
    currentParticipant,
    currentEventParticipant,
    dolbyToken,
    teleportingToRoom,
    teleportEnabled,
    token,
    idToken,
    websocketSessionId,
    user,
    userPermissions,
    generateNewGameliftStreamsSessionKey,
  });

  const { avatarMessage } = useAvatarMessage({
    currentSkin: { avatarId: user?.activeAvatarId || '' } as IAvatarSkin,
    currentEvent,
    customAvatarUrl: user?.customAvatarUrl || '',
  });

  const {
    connectedPod,
    coreweaveLoading,
    dataChannelOpened,
    handleInitializeButton,
    isPlayButtonPressed,
    playStream,
    rootElement,
    setRootElement,
    setWebSocketAddress,
    togglePlayStream,
    webSocketAddress,
  } = useCoreweave({
    currentEvent,
    currentParticipant,
    currentRoom,
    gameSound,
    initMessageHandler,
    initMessageSended,
    muted,
    pixelStreamingClient,
    coreweavePixelStreaming,
    setGameReadyToPlay,
    setInitMessageSended,
    setCoreweavePixelStreaming,
    ue5CoreWeaveDisabled,
    streamingProvider,
    avatarMessage,
  });

  const {
    streamId: gameCastStreamId,
    setStreamId: setGameCastStreamId,
    streamRegion: gameCastStreamRegion,
    setStreamRegion: setGameCastStreamRegion,
  } = useInitGameCastStream(
    currentEvent,
    streamingProvider,
    pixelStreamingClient,
  );

  const {
    isStreamLoaded,
    isStreamLoading,
    setIsStreamLoaded,
    setIsStreamLoading,
    videoRef,
    audioRef,
    abortGamecastConnection,
    processActiveStreamSession,
    gamecastClientRef,
    sendMytaverseActions,
    initGameliftStreamsSessionStatusTimeout,
    clearGameliftStreamsSessionStatusTimeout,
    setReconnectGamecast,
    resetReconnectGamecastAttempts,
  } = useGamecast({
    currentEventId,
    currentRoom,
    trackAnalytics: trackEventAnalytics,
    gameSound,
    currentParticipant,
    setGameCastStreamId,
    initMessageHandler,
    gameCastStreamRequestSent: gameCastStreamRequestSended,
    setGameCastStreamRequestSent: setGameCastStreamRequestSended,
    setInitMessageSent: setInitMessageSended,
    gameCastStreamId,
    gameCastStreamRegion,
    setGameCastStreamRegion,
    setStreamingProvider,
    setIsGamecastCrash,
    setCurrentRoom,
    setCurrentParticipant,
    isGamecastCrash,
    currentEventParticipant,
    websocketSessionId,
    currentEvent,
    gamecastStats,
    generateNewGameliftStreamsSessionKey,
    isGamecastTimeout,
    setIsGamecastTimeout,
    pixelStreamingClient,
    externalKeyboardConnected,
    setShowExternalKeyboardOverlay,
  });

  const handleUEInitializationTimeout = useCallback(() => {
    mytaverseEventLogger.log(
      `UE initialization stuck for ${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds`,
    );

    setReconnectGamecast(true);
  }, [setReconnectGamecast]);

  const clearUEInitializationTimeout = useCallback(() => {
    if (ueInitializationTimeoutRef.current) {
      clearTimeout(ueInitializationTimeoutRef.current);
      ueInitializationTimeoutRef.current = null;
    }
  }, []);

  const initUEInitializationTimeout = useCallback(() => {
    clearUEInitializationTimeout();

    mytaverseEventLogger.log(
      `Start UE initialization timer (${REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS} seconds)`,
    );

    ueInitializationTimeoutRef.current = setTimeout(
      handleUEInitializationTimeout,
      Number(REACT_APP_UE_INITIALIZATION_TIMEOUT_SECONDS) * 1000,
    );
  }, [
    setIsGamecastTimeout,
    clearUEInitializationTimeout,
    handleUEInitializationTimeout,
  ]);

  const handleGamecastStreamSession = useCallback(
    async ({
      status,
      gamecastStreamSession,
      sessionKey,
    }: {
      status: StreamSessionStatus;
      gamecastStreamSession: IStartStreamSessionOutput | null;
      sessionKey?: string;
    }) => {
      const expectedSessionKey = gameliftStreamsSessionKeyRef.current;
      const isCurrentAttempt =
        !!sessionKey && !!expectedSessionKey && expectedSessionKey === sessionKey;

      mytaverseEventLogger.log(
        `Handle WS message: sessionId: ${
          gamecastStreamSession?.Arn
            ? gamecastStreamSession?.Arn.split('/').pop()
            : ''
        }, status: ${status}, sessionKey: ${sessionKey}`,
      );


      if (isCurrentAttempt) {
        clearGameliftStreamsSessionStatusTimeout();
      } else {
        mytaverseEventLogger.debug(
          `Skip side-effects for unknown sessionKey: expected ${expectedSessionKey}, got ${sessionKey}`,
        );
      }

      switch (status) {
        case StreamSessionStatus.ACTIVATING: {
          if (isCurrentAttempt) {
            initGameliftStreamsSessionStatusTimeout();
          }
          break;
        }

        case StreamSessionStatus.ACTIVE: {
          if (!isCurrentAttempt) {
            break;
          }

          if (!gamecastStreamSession) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
            break;
          }

          const { isStarted, isAborted } = await processActiveStreamSession(
            gamecastStreamSession,
          );

          if (isStarted) {
            resetReconnectGamecastAttempts();
            trackEventAnalytics(AnalyticsType.FinishGameLiftStreamsInit);
            trackEventAnalytics(AnalyticsType.StartUeInitialization);
            setIsStreamLoading(false);
            setIsStreamLoaded(true);

            if (currentEventParticipant) {
              sendMytaverseActions(currentEventParticipant);
            }

            initUEInitializationTimeout();

            mytaverseEventLogger.log(
              `Started gamelift streams session: ${gamecastStreamSession.Arn?.split(
                '/',
              ).pop()}`,
            );
          } else if (!isAborted) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false, // or true?
            });
          }

          break;
        }

        case StreamSessionStatus.ERROR: {
          if (!isCurrentAttempt) {
            break;
          }

          mytaverseEventLogger.log('Gamecast connection retry');
          setReconnectGamecast(true);

          break;
        }

        case StreamSessionStatus.NO_MACHINES: {
          if (isCurrentAttempt) {
            navigateToEventsPage({
              message: 'notifications.noAvailableStreamingProviders',
              isModal: false,
            });
          }
          break;
        }

        default:
          break;
      }
    },
    [
      processActiveStreamSession,
      setIsStreamLoading,
      setIsStreamLoaded,
      currentEventParticipant,
      sendMytaverseActions,
      navigateToEventsPage,
      pixelStreamingClient,
      initGameliftStreamsSessionStatusTimeout,
      clearGameliftStreamsSessionStatusTimeout,
      setReconnectGamecast,
      initUEInitializationTimeout,
    ],
  );

  const loadSessionIds = useCallback(() => {
    const websocketSessionIdsStr = sessionStorage.getItem('websocketSessionId');

    return websocketSessionIdsStr ? JSON.parse(websocketSessionIdsStr) : [];
  }, []);

  useEffect(() => {
    if (!websocketSessionId) {
      return;
    }

    const websocketSessionIds = loadSessionIds();
    websocketSessionIds.push(websocketSessionId);

    sessionStorage.setItem(
      'websocketSessionId',
      JSON.stringify(websocketSessionIds),
    );
  }, [websocketSessionId]);

  useEffect(() => {
    mytaverseEventLogger.log(`Open event ${currentEventId} page`);
    return () => {
      mytaverseEventLogger.log(`Leave event ${currentEventId} page`);
      clearUEInitializationTimeout();
      clearGameliftStreamsSessionStatusTimeout();
      abortGamecastConnection();
      if (!isLogoutClicked()) {
        sendJSONMessageToWebSocket({
          action: 'LEAVE_EVENT',
        });
      }
    };
  }, []);

  const cleanStates = useCallback(() => {
    setRooms([]);
    setParticipants([]);
    setOpenChat(false);
    setCurrentParticipant(undefined);
    setCurrentRoom(null);
    setCurrentEvent(undefined);
    setTeleportEnabled(false);
  }, [
    setRooms,
    setParticipants,
    setCurrentParticipant,
    setCurrentRoom,
    setCurrentEvent,
    sendJSONMessageToWebSocket,
  ]);

  useBackButton({
    cleanStates,
    initMessageSended,
    ue5WebsocketConnected,
    setUe5WebsocketConnected,
    currentEventId: currentEventId ?? null,
  });

  useEffect(() => {
    if (currentRoom) {
      localStorage.setItem('currentRoomId', currentRoom.roomId);
      mytaverseEventLogger.log(`Room changed to ${currentRoom.name}`);

      mytaverseEventLogger.log('Clear UE initialization timeout');
      clearUEInitializationTimeout();
    }
  }, [currentRoom, clearUEInitializationTimeout]);

  useEffect(() => {
    if (userFiles) {
      localStorage.setItem('userFiles', JSON.stringify(userFiles) as string);
    }
  }, [userFiles, currentRoom]);

  useEffect(() => {
    if (currentRoom) {
      setUserFiles([]);
    }
  }, [currentRoom]);

  useAsyncEffect(async () => {
    if (!currentRoom) {
      return;
    }

    if (pointsOfInterest) {
      setPointsOfInterest(null);
    }

    let pointsInterest: IPointOfInterest[] = [];

    try {
      pointsInterest = await EventsService.getPointOfInterest(
        currentRoom.roomId,
      );
    } catch (e) {
      pointsInterest = [];
    }

    if (pointsInterest?.length) {
      setPointsOfInterest(pointsInterest);
    }
  }, [currentRoom]);

  useEffect(() => {
    if (!currentEventId) {
      return;
    }

    sendJSONMessageToWebSocket({
      action: muted
        ? SendMutedStateMessageEnum.MUTE
        : SendMutedStateMessageEnum.UNMUTE,
      timestamp: Date.now(),
    });
  }, [muted, currentEventId, sendJSONMessageToWebSocket]);

  const canSpeak = useMemo(() => {
    return getClientSpecificCanSpeak(currentRoom);
  }, [currentRoom]);

  const canShowCamera = useMemo(() => {
    return getClientSpecificCanShowWebCameraOnAvatar(currentRoom);
  }, [currentRoom]);

  const canShareCamera = useMemo(() => {
    return getClientSpecificCanShareWebCameraOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  const canShareScreen = useMemo(() => {
    return getClientSpecificCanShareScreenOnScreen(
      currentRoom,
      currentEvent,
      userPermissions,
    );
  }, [currentEvent, currentRoom, userPermissions]);

  const shouldConnectToConference = useMemo(() => {
    return !skyLinkExperience;
  }, [skyLinkExperience]);

  useAsyncEffect(async () => {
    if (loadingNewParticipants) {
      return;
    }
    const loadingParticipants = participants.filter(
      (p) => p.state === ParticipantState.Loading,
    );
    if (loadingParticipants.length === 0 || !currentEventId) {
      return;
    }
    setLoadingNewParticipants(true);
    const eventParticipant = await EventsService.getEvent(currentEventId);
    if (eventParticipant.event?.participants?.length !== 0) {
      setParticipants((prev) => {
        return prev.map((participant) => {
          if (participant.state === ParticipantState.Loaded) {
            return participant;
          }
          const loadedParticipant = eventParticipant.event?.participants.find(
            (p) => p.participantId === participant.participantId,
          );
          if (!loadedParticipant) {
            return participant;
          }
          const { name } = loadedParticipant;
          return {
            ...participant,
            state: ParticipantState.Loaded,
            name: name,
            fullName: name,
          };
        });
      });
    }
    setLoadingNewParticipants(false);
  }, [loadingNewParticipants, participants, currentEventId]);

  useEffect(() => {
    if (currentRoom && currentRoom.dolbySpatialAudioScale) {
      setCurrentRoomDolbySpatialAudioScale(currentRoom.dolbySpatialAudioScale);
    }
  }, [currentRoom]);

  // TODO merge it with load participant settings data
  useAsyncEffect(async () => {
    try {
      if (currentEventId && userId) {
        setLoading(true);
        setEventLoaded(false);

        setPreviousCustomAvatar(null);
        setPreviousSkin(null);

        let events: IParticipantEvents;
        let eventParticipant: IEventParticipant;

        try {
          [events, eventParticipant] = await Promise.all([
            ParticipantsService.getCurrentParticipantEvents(),
            EventsService.getEvent(currentEventId),
          ]);
        } catch (e: any) {
          return navigateToEventsPage({
            message: 'notifications.loadEventDataError',
            type: NOTIFICATION_TYPES.ERROR
          });
        }

        setEvents(
          events.events.map(
            (participantEvent) => participantEvent.event as IEvent,
          ),
        );
        setCurrentEvent(eventParticipant.event);
        if (
          eventParticipant.event &&
          eventParticipant.event.loadingScreenAsset &&
          typeof eventParticipant.event.loadingScreenAsset !== 'string' &&
          'url' in eventParticipant.event.loadingScreenAsset
        ) {
          preloadVideos([
            (eventParticipant.event.loadingScreenAsset as any).url as string,
          ]);
        }

        setTeleportEnabled(
          eventParticipant.event?.eventTags
            ? eventParticipant.event.eventTags
                .map((tag) => tag.toLowerCase())
                .every(
                  (tag) => !['officehours', 'baseofficehours'].includes(tag),
                )
            : true,
        );

        const rooms = eventParticipant.event?.rooms || [];

        setRooms(rooms);
        const defaultRoom = rooms.find((room) => room.isDefault);
        setTeleportingToRoom(defaultRoom || null);

        const participants = eventParticipant.event?.participants
          ? eventParticipant.event.participants.map((participant) => ({
              ...participant,
              fullName: participant.name,
              state: ParticipantState.Loaded,
            }))
          : [];

        setParticipants(participants);

        const currentParticipant = participants.find(
          (participant) => participant.participantId === userId,
        );

        if (currentParticipant) {
          setCurrentParticipant({ ...currentParticipant });
        }

        const { event, ...currentEventParticipant } = eventParticipant;
        setCurrentEventParticipant(currentEventParticipant);

        if (eventParticipant.event?.streamService) {
          setStreamService(eventParticipant.event?.streamService);
        }

        setDolbyToken(eventParticipant.dolbyToken as string);
        setStreamChatToken(eventParticipant.streamChatToken as string);
        setStreamChatMemberId(eventParticipant.streamChatMemberId as string);

        if (user?.roleScopes) {
          setUserPermissions((prevState: ParticipantScopes[]) => {
            if (prevState.length > 0) {
              return prevState;
            }

            return getClientSpecificUserEventPermissions({
              role: eventParticipant.role,
              roleScopes: user.roleScopes,
            });
          });

          const newUserRole = getClientSpecificUserEventRole();

          if (newUserRole !== null) {
            setCurrentEventParticipant(
              (prevState: IEventParticipant | null) => {
                if (prevState) {
                  return {
                    ...prevState,
                    role: newUserRole as EventRole,
                  };
                }

                return prevState;
              },
            );

            setCurrentParticipant((prevState) => {
              if (prevState) {
                return {
                  ...prevState,
                  role: newUserRole,
                };
              }

              return prevState;
            });
          }
        }

        // const followingData = await EventsService.getFollowingData(
        //   currentEventId,
        // );
        //
        // const { pending, accepted } = mapFollowers(followingData.followers);
        //
        // setPendingFollowersData(pending);
        // setAcceptedFollowersData(accepted);

        setLoading(false);
      } else {
        setCurrentEvent(undefined);
        setTeleportEnabled(false);

        setRooms([]);
        setCurrentRoom(null);

        setParticipants([]);
        setCurrentParticipant(undefined);
      }
    } catch (error) {
      showNotification(
        getBadRequestNotification({ message: (error as Error).message }),
      );
    }
  }, [userId, currentEventId, user?.roleScopes]);

  const ownerTeleportToRoomPreserveData = React.useCallback(() => {
    if (acceptedFollowersData.length === 0 || !currentParticipant) return;

    const followers = acceptedFollowersData
      .filter(
        (follower) => follower.adminId === currentParticipant.participantId,
      )
      .map((follower) => follower.userId);

    if (followers.length === 0) return;

    setPreserveAcceptedFollowersId([...new Set(followers)]);
  }, [currentParticipant, acceptedFollowersData]);

  const ownerTeleportToRoom = React.useCallback(() => {
    if (
      preserveAcceptedFollowersId.length === 0 ||
      !currentParticipant ||
      !currentEventId
    )
      return;

    sendJSONMessageToWebSocket({
      action: WebsocketAction.OwnerTeleport,
      ownerId: currentParticipant.participantId,
      followerIds: [...preserveAcceptedFollowersId],
      ownerRoomId: currentParticipant.roomId,
      gameSessionId: currentParticipant.gameSessionId || null,
    });
  }, [
    currentParticipant,
    acceptedFollowersData,
    preserveAcceptedFollowersId,
    sendMessageToEvent,
    currentEventId,
  ]);

  useEffect(() => {
    if (isParticipantRoomChanged) {
      ownerTeleportToRoom();
      setIsParticipantRoomChanged(false);
      setPreserveAcceptedFollowersId([]);
    }
  }, [
    isParticipantRoomChanged,
    ownerTeleportToRoom,
    setPreserveAcceptedFollowersId,
  ]);

  useEffect(() => {
    if (
      currentEvent &&
      currentParticipant &&
      currentParticipant.eventId !== currentEvent.eventId
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const ref = urlParams.get('ref') || 'hub';

      sendJSONMessageToWebSocket({
        action: 'JOIN_TO_EVENT',
        timestamp: Date.now(),
        eventId: currentEvent.eventId,
        ref,
      });
    }
  }, [currentEvent, currentParticipant]);

  const updateParticipant: UpdateParticipantType = useCallback(
    (participantId, newParticipantInfo) => {
      if (
        currentParticipant &&
        currentParticipant.participantId === participantId
      ) {
        setCurrentParticipant((prevParticipant) => ({
          ...prevParticipant,
          ...newParticipantInfo,
          isSpeaker: !!prevParticipant?.isSpeaker,
        }));
      }

      setParticipants((prevParticipants) =>
        prevParticipants.map((prevParticipant) =>
          prevParticipant.participantId === participantId
            ? {
                ...prevParticipant,
                ...newParticipantInfo,
              }
            : prevParticipant,
        ),
      );
    },
    [currentParticipant],
  );

  const resetPreviousSkin = useCallback(() => {
    if (previousSkin) {
      setPreviousSkin(null);
    }
  }, [previousSkin, setPreviousSkin]);

  const resetPreviousCustomSkin = useCallback(() => {
    if (previousCustomAvatar) {
      setPreviousCustomAvatar(null);
    }
  }, [previousCustomAvatar, setPreviousCustomAvatar]);

  const setRoomDolbySpatialAudioScale = useCallback(
    (roomId: string, dolbySpatialAudioScale: number) => {
      if (currentRoom && currentRoom.roomId === roomId) {
        setCurrentRoomDolbySpatialAudioScale(dolbySpatialAudioScale);
      }
      /*
      setCurrentRoom((prev) => {
        if (!prev || prev.id !== roomId) {
          return prev;
        }

        return {
          ...prev,
          dolbySpatialAudioScale,
        };
      });
       */

      setRooms((prev) =>
        prev.map((room) => {
          if (room.roomId === roomId) {
            return {
              ...room,
              dolbySpatialAudioScale,
            };
          }

          return room;
        }),
      );
    },
    [currentRoom],
  );

  const updateRoomScale = useCallback(
    async (value: number) => {
      if (currentRoom && currentEventId) {
        await EventsService.updateRoomSpatialScale(
          currentRoom.roomId as string,
          value,
          currentEventId,
        );

        setRoomDolbySpatialAudioScale(currentRoom.roomId, value);
      }
    },
    [currentRoom, currentEventId, setRoomDolbySpatialAudioScale],
  );

  const setSpeakingParticipants = React.useCallback(
    (speakingParticipantIds: string[]) => {
      if (currentParticipant) {
        const speaking: boolean =
          speakingParticipantIds.indexOf(currentParticipant.participantId) !==
          -1;

        if (currentParticipant.speaking !== speaking) {
          setCurrentParticipant({
            ...currentParticipant,
            speaking,
          });
        }
      }
    },
    [currentParticipant, setCurrentParticipant, setParticipants],
  );

  const teleportToRoom = React.useCallback(
    (room: IRoom | null) => {
      setTeleportingToRoom(room);
      //TODO add is speaker to message
      if (room && currentParticipant && !isTeleporting) {
        MytaverseLogger.log(`Teleporting to room: ${room.roomId} ${room.name}`);
        setIsTeleporting(true);
        ownerTeleportToRoomPreserveData();
        sendJSONMessageToWebSocket({
          action: WebsocketAction.TeleportToRoom,
          roomId: room.roomId,
          isSpeaker: currentParticipant.isSpeaker,
        });
      }
    },
    [
      setTeleportingToRoom,
      sendJSONMessageToWebSocket,
      currentParticipant,
      setIsTeleporting,
      isTeleporting,
      ownerTeleportToRoomPreserveData,
    ],
  );

  const teleportToParticipant = React.useCallback(
    async (participant: IParticipant | null) => {
      if (!participant || !currentRoom) {
        return;
      }

      MytaverseLogger.log(
        `Teleporting to participant: ${participant.participantId} ${participant.fullName}`,
      );

      // const { roomId } = await ParticipantsService.getParticipantRoom(
      //   participant.participantId,
      // );

      // if (currentRoom.roomId !== roomId) {
      //   setIsTeleporting(true);
      //   ownerTeleportToRoomPreserveData();
      // }

      sendMessageToUnreal({
        action: WebsocketAction.TeleportToParticipant,
        participantId: participant.participantId,
        roomId: currentRoom?.roomId,
      });
    },
    [currentRoom, sendMessageToUnreal, ownerTeleportToRoomPreserveData],
  );

  const teleportRef = useRef<NodeJS.Timeout | null>(null);
  const ue5WebsocketConnectedRef = useRef(false);

  useEffect(() => {
    ue5WebsocketConnectedRef.current = ue5WebsocketConnected;
  }, [ue5WebsocketConnected]);

  useEffect(() => {
    if (isTeleporting && currentRoom && !teleportRef.current) {
      teleportRef.current = setInterval(() => {
        if (ue5WebsocketConnectedRef.current && teleportRef.current) {
          setIsTeleporting(false);
          clearInterval(teleportRef.current);
        }
      }, 5000);

      return;
    }

    if (teleportRef.current && !isTeleporting) {
      clearInterval(teleportRef.current);
      teleportRef.current = null;
      return;
    }

    return () => {
      if (teleportRef.current) {
        clearInterval(teleportRef.current);
        teleportRef.current = null;
      }
    };
  }, [isTeleporting, currentRoom]);

  useEffect(() => {
    if (isTeleportingToRoomByUnreal) {
      ownerTeleportToRoomPreserveData();
    }
  }, [isTeleportingToRoomByUnreal, ownerTeleportToRoomPreserveData]);

  useEffect(() => {
    if (
      currentEvent?.eventTags?.includes('ExtraLaunchArgsRequired') &&
      (!launchArgs || launchArgs.length === 0)
    ) {
      mytaverseEventLogger.log(
        'Redirect to events page because no extra launch args',
      );
      navigateToEventsPage({ message: 'notifications.noExtraLaunchArgs' });
    }
  }, [currentEvent?.eventTags, launchArgs, navigateToEventsPage]);

  useEffect(() => {
    if (movementCollisionIdentified) {
      MytaverseLogger.log('Movement collision identified. Reset pressed keys');
      const videoElement = document.getElementById('gamecastVideoTag');

      if (videoElement) {
        const keysPressed = [
          'W',
          'A',
          'S',
          'D',
          'ArrowUp',
          'ArrowLeft',
          'ArrowDown',
          'ArrowRight',
        ].map((key) => {
          return {
            key,
            code: key.length === 1 ? `Key${key}` : key,
            bubbles: true,
            cancelable: true,
          };
        });

        Object.values(keysPressed).map((event) => {
          MytaverseLogger.log(`Dispatch keyup: ${event.key}`);
          videoElement.dispatchEvent(new KeyboardEvent('keyup', event));
        });
      }
      setMovementCollisionIdentified(false);
    }
  }, [movementCollisionIdentified]);

  const closeLeftMenu = React.useCallback(() => {
    setOpenLeftMenu(false);
  }, [setOpenLeftMenu]);

  const openLeftMenuHandler = React.useCallback(
    (tab: any) => {
      setOpenLeftMenu(true);
      if (tab) {
        setLeftMenuTab(tab);
      }
    },
    [setOpenLeftMenu, setLeftMenuTab],
  );

  const setMutedHandler = React.useCallback(
    (value: boolean | undefined) => {
      setMuted(value || !muted);
      trackEventAnalytics(
        value || !muted ? AnalyticsType.MicOff : AnalyticsType.MicOn,
      );
    },
    [muted, setMuted, trackEventAnalytics],
  );

  const toggleRaiseHand = React.useCallback(() => {
    const nextState = !isHandRaised;

    setIsHandRaised(nextState);

    sendJSONMessageToWebSocket({
      action: 'RAISE_HAND',
      active: nextState ? true : false,
    });

    trackEventAnalytics(AnalyticsType.NavbarButtonClick, {
      name: 'raise-hand',
      active: nextState,
    });
  }, [isHandRaised, sendJSONMessageToWebSocket, trackEventAnalytics]);

  const setEmojiHandler = React.useCallback(
    (emoji: string | null) => {
      setEmoji(emoji);

      sendJSONMessageToWebSocket({
        action: 'SEND_EMOJI',
        emoji,
      });
    },
    [setEmoji, sendJSONMessageToWebSocket],
  );

  const setReactionHandler = React.useCallback(
    (reaction: string | null) => {
      setReaction(reaction);

      sendJSONMessageToWebSocket({
        action: 'TOGGLE_REACTION',
        reactionId: reaction,
      });
      trackEventAnalytics(AnalyticsType.ToggleReaction, { reaction });
    },
    [setReaction, sendJSONMessageToWebSocket, trackEventAnalytics],
  );

  const setSharingWindowFullscreenHandle = useCallback(() => {
    setSharingWindowFullscreen(!sharingWindowFullsceen);
  }, [setSharingWindowFullscreen, sharingWindowFullsceen]);

  if (loading) {
    return <EventFullLoadingPage />;
  }

  return (
    <Outlet
      context={{
        currentEventId: currentEventId as string,
        currentParticipantId: userId as string,

        currentEventParticipant,
        playVideoUrl,
        setPlayVideoUrl,
        isOpenLeftMenu: openLeftMenu,
        reaction,
        emoji,
        muted,
        eventLoaded,
        userFiles,
        previousSkin,
        previousCustomAvatar,
        leftMenuTab,
        rooms,
        currentEvent,
        currentRoom,
        currentRegion,
        currentParticipant,
        clientOptions,
        gameCastStreamRequestSended,
        isTeleporting,
        setIsTeleporting,
        setGameCastStreamRequestSended,
        setCurrentParticipant,
        setCurrentEventParticipant,
        initMessageHandler,
        gameReadyToPlay,
        setGameReadyToPlay,
        participants,
        teleportingToRoom,
        teleportEnabled,
        pointsOfInterest,
        loading,
        dolbyToken,
        streamChatToken,
        streamChatMemberId,
        setLeftMenuScrollHandler,
        streamService,
        openLeaveRegionDialog,
        leftMenuScrollHandler,
        showControls,
        poiPreviewSrc,
        initMessageSended,
        sharingWindowFullsceen,
        participantsSound,
        selectedTeleportRoom,
        gameSound,
        openTeleportToRoomDialog,
        openAdminSettingsModal,
        openCameraPublisherDialog,
        setOpenCameraPublisherDialog,
        isStreamLoaded,
        isStreamLoading,
        setIsStreamLoaded,
        setIsStreamLoading,
        //streamSession,
        videoRef,
        audioRef,
        setOpenAdminSettingsModal,
        streamingProvider,
        gameCastStreamId,
        developersTerminalMessages,
        setDevelopersTerminalMessages,
        setGameCastStreamId,
        gameCastStreamRegion,
        setGameCastStreamRegion,
        setStreamingProvider,
        setGameSound: setGameSoundHandle,
        setUe5WebsocketConnected,
        ue5WebsocketConnected,
        isMinimizedScreenSharing,
        openDevelopersTerminal,
        setOpenDevelopersTerminal,
        setIsMinimizedScreenSharing,
        setOpenTeleportToRoomDialog,
        canSpeak,
        canShowCamera,
        canShareCamera,
        canShareScreen,
        shouldConnectToConference,
        setSelectedTeleportRoom,
        setParticipantsSound: setParticipantsSoundHandle,
        setOpenLeaveRegionDialog,
        setSharingWindowFullscreen: setSharingWindowFullscreenHandle,
        setInitMessageSended,
        setPoiPreviewSrc,
        setShowControls,
        setParticipants,
        openLeftMenu: openLeftMenuHandler,
        closeLeftMenu,
        setReaction: setReactionHandler,
        setEventLoaded,
        setMuted: setMutedHandler,
        setEmoji: setEmojiHandler,
        isHandRaised,
        setIsHandRaised,
        toggleRaiseHand,
        setUserFiles,
        resetPreviousSkin,
        resetPreviousCustomSkin,
        setLeftMenuTab,
        setCurrentRoom,
        setCurrentRegion,
        currentRoomDolbySpatialAudioScale,
        cleanStates,
        connectedPod,
        coreweaveLoading,
        dataChannelOpened,
        handleInitializeButton,
        isPlayButtonPressed,
        pixelStreaming: coreweavePixelStreaming,
        playStream,
        rootElement,
        setRootElement,
        setWebSocketAddress,
        togglePlayStream,
        webSocketAddress,
        setTeleportingToRoom,
        teleportToRoom,
        teleportToParticipant,
        isTeleportingToRoomByUnreal,
        setIsTeleportingToRoomByUnreal,
        setParticipantState,
        updateParticipant,
        setSpeakingParticipants,

        setRoomDolbySpatialAudioScale,
        updateRoomScale,
        showAvaturnModal,
        setShowAvaturnModal,
        recreateAvaturnAvatar,
        setRecreateAvaturnAvatar,
        trackEventAnalytics,
        pixelStreamingClient,
        pendingFollowersData,
        setPendingFollowersData,
        acceptedFollowersData,
        setAcceptedFollowersData,
        enableSoulmachinesAssistant,
        setEnableSoulmachinesAssistant,
        ue5CoreWeaveDisabled,
        setUe5CoreWeaveDisabled,
        isGamecastCrash,
        setIsGamecastCrash,
        events,
        gamecastStats,
        showGamecastStats,
        setShowGamecastStats,
        showExitButton,
        setShowExitButton,
        isHideWebUI,
        setIsHideWebUI,
        isHideWebUIExceptAssistant,
        setIsHideWebUIExceptAssistant,
        isHideAllExceptTopBar,
        setIsHideAllExceptTopBar,
        isSkyLinkActive,
        setIsSkyLinkActive,
        screenshotDownloadUrl,
        setScreenshotDownloadUrl,
        fallbackScreenshotDownloadUrlRef,
        isUEModalActionInProgress,
        setIsUEModalActionInProgress,
        processActiveStreamSession,
        gamecastClientRef,
        sendMytaverseActions,
        handleGamecastStreamSession,
        gameliftStreamsSessionKey: gameliftStreamsSessionKeyRef.current,
        generateNewGameliftStreamsSessionKey,
        skyLinkExperience,
        showExternalKeyboardOverlay,
        setShowExternalKeyboardOverlay,
        externalKeyboardConnected,
        setExternalKeyboardConnected,
        isUserEnteringEvent,
        setIsUserEnteringEvent,
        showCustomSpotsEditor,
        setShowCustomSpotsEditor,
        roomSpots,
        setRoomSpots,
        selectedCustomizationUeId,
        setSelectedCustomizationUeId,
        selectedCustomizationTargetSize,
        setSelectedCustomizationTargetSize,
        familiarizeState,
        setFamiliariseState,
        movementCollisionIdentified,
        setMovementCollisionIdentified,
      }}
    />
  );
};
